/**
 * Debounced search input with a leading icon, a clear button when non-empty,
 * and an optional result-count slot rendered to the right of the input.
 *
 * Debounces by default at 300 ms — query updates fire on the trailing edge
 * so users typing quickly don't hammer the backend. The input itself is
 * controlled with the *immediate* value (so users see their keystrokes),
 * while parent state receives the debounced value.
 */
import { useEffect, useState } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

type Props = {
  /** Current debounced value held by the parent. */
  value: string
  /** Called with the debounced value, on the trailing edge. */
  onDebouncedChange: (value: string) => void
  placeholder?: string
  /** Debounce window in ms. Default 300. */
  delayMs?: number
  /** Optional element rendered right of the input — e.g. "5 results". */
  trailing?: React.ReactNode
  className?: string
  /** Restricts the max width on the wrapper, defaults to max-w-md. */
  maxWidthClass?: string
  /** Optional id for screen readers / labels. */
  inputId?: string
}

export function DebouncedSearchInput({
  value,
  onDebouncedChange,
  placeholder,
  delayMs = 300,
  trailing,
  className,
  maxWidthClass = 'max-w-md',
  inputId,
}: Props) {
  const { t } = useLanguage()
  const effectivePlaceholder = placeholder ?? t('admin.field.search', 'Search…')
  const [local, setLocal] = useState(value)

  // Keep local in sync if the parent resets value externally (e.g. clear filter)
  useEffect(() => {
    setLocal(value)
  }, [value])

  // Debounce trailing edge
  useEffect(() => {
    if (local === value) return
    const t = setTimeout(() => {
      onDebouncedChange(local)
    }, delayMs)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, delayMs])

  return (
    <div className={`flex items-center gap-3 ${className ?? ''}`}>
      <div className={`relative flex-1 ${maxWidthClass}`}>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id={inputId}
          type="search"
          placeholder={effectivePlaceholder}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          className="pl-9 pr-9"
        />
        {local && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => {
              setLocal('')
              onDebouncedChange('')
            }}
            aria-label={t("admin.aria.clearSearch", "Clear search")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {trailing && (
        <div className="text-xs text-muted-foreground whitespace-nowrap">{trailing}</div>
      )}
    </div>
  )
}
